import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, Package, MessageCircle, DollarSign, Coins, Check, X, Star, Search, Trash2, RotateCcw, Flame, TrendingUp, TrendingDown, Rocket, Activity, Pencil, Loader2, Tv, Upload, Zap, FileText, Inbox, Mail, MailOpen, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  adminOverview, adminAnalytics, adminApps, adminApprove, adminSuspend, adminFeature, adminUnfeature, adminEditorsChoice,
  adminSoftDelete, adminRestore, adminPurge,
  adminReportedComments, adminDeleteComment, adminClearReports, adminCashouts, adminCashoutAction,
  adminEditApp,
  formatApiError, formatDownloads,
} from "@/lib/api";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";
import { ADMIN } from "@/constants/testIds";
import PowerAdminTab from "@/pages/admin/PowerAdminTab";
import TrashAdminTab from "@/pages/admin/TrashAdminTab";
import ReviewsAdminTab from "@/pages/admin/ReviewsAdminTab";
import EarningsAdminTab from "@/pages/admin/EarningsAdminTab";
import SsoAdminTab from "@/pages/admin/SsoAdminTab";
import NotificationsAdminTab from "@/pages/admin/NotificationsAdminTab";
import AppGeneratorTab from "@/pages/admin/AppGeneratorTab";
import ClickerAdminTab from "@/pages/admin/ClickerAdminTab";
import WalkieAdminTab from "@/pages/admin/WalkieAdminTab";
import VibeAdminTab from "@/pages/admin/VibeAdminTab";
import CloudAdminTab from "@/pages/admin/CloudAdminTab";

const TABS = [
  { id: "overview", label: "Overview", icon: <Shield className="w-4 h-4" />, testId: ADMIN.tabOverview },
  { id: "apps", label: "Apps", icon: <Package className="w-4 h-4" />, testId: ADMIN.tabApps },
  { id: "tv", label: "TV Channels", icon: <Tv className="w-4 h-4" />, testId: ADMIN.tabTv },
  { id: "pages", label: "Pages", icon: <FileText className="w-4 h-4" />, testId: "admin-tab-pages" },
  { id: "inbox", label: "Inbox", icon: <Inbox className="w-4 h-4" />, testId: "admin-tab-inbox" },
  { id: "comments", label: "Comments", icon: <MessageCircle className="w-4 h-4" />, testId: ADMIN.tabComments },
  { id: "cashouts", label: "Legacy coin cash-outs", icon: <DollarSign className="w-4 h-4" />, testId: ADMIN.tabCashouts },
  { id: "users", label: "Users", icon: <Users className="w-4 h-4" />, testId: ADMIN.tabUsers },
  { id: "power", label: "Power", icon: <Zap className="w-4 h-4" fill="currentColor" />, testId: "admin-tab-power" },
  { id: "trivia", label: "Trivia", icon: <Zap className="w-4 h-4" />, testId: "admin-tab-trivia" },
  { id: "trash", label: "Trash Hunter", icon: <Shield className="w-4 h-4" />, testId: "admin-tab-trash" },
  { id: "earnings", label: "Earnings & Cash-outs", icon: <DollarSign className="w-4 h-4" />, testId: "admin-tab-earnings" },
  { id: "appgen", label: "AI App Generator", icon: <Zap className="w-4 h-4" />, testId: "admin-tab-appgen" },
  { id: "notifs", label: "Notifications", icon: <Activity className="w-4 h-4" />, testId: "admin-tab-notifications" },
  { id: "sso", label: "Jixxeo Sign-in", icon: <Shield className="w-4 h-4" />, testId: "admin-tab-sso" },
  { id: "reviews", label: "Reviews", icon: <Star className="w-4 h-4" />, testId: "admin-tab-reviews" },
  { id: "clicker", label: "Clicker", icon: <Zap className="w-4 h-4" />, testId: "admin-tab-clicker" },
  { id: "walkie", label: "Walkie Talkies", icon: <Activity className="w-4 h-4" />, testId: "admin-tab-walkie" },
  { id: "vibe", label: "JixxeoVibe", icon: <Activity className="w-4 h-4" />, testId: "admin-tab-vibe" },
  { id: "cloud", label: "Jixxeo Cloud", icon: <Activity className="w-4 h-4" />, testId: "admin-tab-cloud" },
];

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); toast.error("Admin only"); }
  }, [user, loading, nav]);

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div data-testid={ADMIN.root}>
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FF003C] mb-3 flex items-center gap-2">
          <Shield className="w-3 h-3" /> // admin console · restricted
        </div>
        <h1 className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tighter">Admin Panel</h1>

        <div className="flex gap-1 mt-8 border-b border-white/10 overflow-x-auto hide-scrollbar">
          {TABS.map((t) => (
            <button key={t.id} data-testid={t.testId} onClick={() => setTab(t.id)}
              className={`mono text-[10px] uppercase tracking-widest px-4 py-3 transition-colors flex items-center gap-2 relative whitespace-nowrap ${
                tab === t.id ? "text-[#FF003C]" : "text-white/50 hover:text-white"
              }`}>
              {t.icon} {t.label}
              {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#FF003C]" />}
            </button>
          ))}
        </div>

        <div className="py-8">
          {tab === "overview" && <Overview />}
          {tab === "apps" && <AppsTab />}
          {tab === "tv" && <TvChannelsTab />}
          {tab === "pages" && <PagesTab />}
          {tab === "inbox" && <InboxTab />}
          {tab === "comments" && <CommentsTab />}
          {tab === "cashouts" && <CashoutsTab />}
          {tab === "users" && <UsersTab />}
          {tab === "power" && <PowerAdminTab />}
          {tab === "trivia" && <TriviaAdminTab />}
          {tab === "trash" && <TrashAdminTab />}
          {tab === "reviews" && <ReviewsAdminTab />}
          {tab === "earnings" && <EarningsAdminTab />}
          {tab === "sso" && <SsoAdminTab />}
          {tab === "notifs" && <NotificationsAdminTab />}
          {tab === "appgen" && <AppGeneratorTab />}
          {tab === "clicker" && <ClickerAdminTab />}
          {tab === "walkie" && <WalkieAdminTab />}
          {tab === "vibe" && <VibeAdminTab />}
          {tab === "cloud" && <CloudAdminTab />}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const [ts, setTs] = useState(null);
  const [range, setRange] = useState(30);
  useEffect(() => { adminOverview().then(setD); }, []);
  useEffect(() => { adminAnalytics(range).then(setTs); }, [range]);
  if (!d) return <div className="mono text-[#00F0FF] animate-pulse">Loading…</div>;
  const cards = [
    { g: "Users", accent: "#00F0FF", items: [
      { l: "Total", v: d.users.total }, { l: "Developers", v: d.users.developers }, { l: "New 7d", v: d.users.new_7d },
    ]},
    { g: "Apps", accent: "#39FF14", items: [
      { l: "Total", v: d.apps.total }, { l: "Suspended", v: d.apps.suspended }, { l: "New 7d", v: d.apps.new_7d },
    ]},
    { g: "Comments", accent: "#FFE300", items: [
      { l: "Total", v: d.comments.total }, { l: "Reported", v: d.comments.reported },
    ]},
    { g: "Revenue", accent: "#FF003C", items: [
      { l: "Tips ($)", v: `$${d.revenue.tips_usd_moved}` },
      { l: "Cashouts ($)", v: `$${d.revenue.cashouts_usd}` },
      { l: "Pending payouts", v: d.revenue.pending_cashouts },
    ]},
    { g: "Ads", accent: "#00F0FF", items: [
      { l: "Views (total)", v: d.ads.views_total },
    ]},
  ];
  return (
    <div className="space-y-6">
      <AnalyticsWidget ts={ts} range={range} onRange={setRange} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.g} className="glass rounded-2xl p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50">{c.g}</div>
            <div className="mt-3 space-y-2">
              {c.items.map((i) => (
                <div key={i.l} className="flex justify-between items-baseline">
                  <span className="mono text-xs text-white/60 uppercase tracking-widest">{i.l}</span>
                  <span className="font-heading font-black text-2xl" style={{ color: c.accent }}>{i.v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsWidget({ ts, range, onRange }) {
  const series = [
    { key: "new_apps",       label: "New app drops", accent: "#39FF14", icon: <Rocket className="w-4 h-4" />,   fmt: (v) => v },
    { key: "tips_coins",     label: "Tips flow",     accent: "#FFE300", icon: <Coins className="w-4 h-4" />,    fmt: (v) => `${v}c` },
    { key: "cashouts_coins", label: "Cashout coins", accent: "#FF003C", icon: <DollarSign className="w-4 h-4" />, fmt: (v) => `${v}c` },
    { key: "active_users",   label: "Active users",  accent: "#00F0FF", icon: <Activity className="w-4 h-4" />, fmt: (v) => v },
  ];
  return (
    <div data-testid="admin-analytics-widget" className="glass rounded-2xl p-5 md:p-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// live analytics</div>
          <div className="font-heading font-black text-2xl uppercase tracking-tighter">Last {range} days</div>
        </div>
        <div className="ml-auto flex items-center gap-1" data-testid="admin-analytics-range">
          {[7, 30, 90].map((r) => (
            <button key={r} onClick={() => onRange(r)}
              data-testid={`admin-analytics-range-${r}`}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${range === r ? "bg-[#00F0FF] border-[#00F0FF] text-black" : "border-white/15 text-white/60 hover:text-white"}`}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      {!ts ? (
        <div className="mono text-xs text-white/50 animate-pulse py-10 text-center">Aggregating {range}-day rollup…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {series.map((s) => {
            const data = ts[s.key] || [];
            const total = ts.totals && ts.totals[s.key] ? ts.totals[s.key] : { sum: 0, delta_pct: 0 };
            return (
              <Spark key={s.key} data={data} labels={ts.labels || []} title={s.label} accent={s.accent}
                icon={s.icon} sum={s.fmt(total.sum)} delta={total.delta_pct} testid={`analytics-${s.key}`} />
            );
          })}
        </div>
      )}
    </div>
  );
}

function Spark({ data, labels, title, accent, icon, sum, delta, testid }) {
  const [hover, setHover] = useState(null);
  const w = 320, h = 88, pad = 6;
  const max = Math.max(1, ...data);
  const step = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
  const pts = data.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - (v / max) * (h - pad * 2);
    return [x, y];
  });
  const path = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  const area = path + ` L${(pad + (data.length - 1) * step).toFixed(1)},${h - pad} L${pad},${h - pad} Z`;
  const deltaUp = (delta || 0) >= 0;
  return (
    <div data-testid={testid} className="glass rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors">
      <div className="flex items-start gap-2">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mono text-[9px] uppercase tracking-widest text-white/50">{title}</div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className="font-heading font-black text-2xl leading-none" style={{ color: accent }}>{sum}</span>
            <span className={`mono text-[10px] uppercase tracking-widest flex items-center gap-0.5 ${deltaUp ? "text-[#39FF14]" : "text-[#FF003C]"}`}>
              {deltaUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {deltaUp ? "+" : ""}{delta || 0}%
            </span>
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-3" preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * w;
          const idx = Math.round((x - pad) / step);
          if (idx >= 0 && idx < data.length) setHover(idx);
        }}
      >
        <defs>
          <linearGradient id={`g-${testid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.5" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#g-${testid})`} />
        <path d={path} fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r={hover === i ? 3.5 : 1.5} fill={accent} opacity={hover === i ? 1 : 0.6} />
        ))}
      </svg>
      <div className="mono text-[9px] uppercase tracking-widest text-white/40 flex justify-between mt-1">
        {hover !== null ? (
          <>
            <span>{labels[hover]}</span>
            <span style={{ color: accent }}>{data[hover]}</span>
          </>
        ) : (
          <>
            <span>{labels[0]}</span>
            <span>{labels[labels.length - 1]}</span>
          </>
        )}
      </div>
    </div>
  );
}

function AppsTab() {
  const [apps, setApps] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // slug being edited
  const load = () => adminApps({ status: status !== "all" ? status : undefined, q: q || undefined }).then((d) => setApps(d.apps));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, q]);

  const act = async (fn, slug, ...args) => {
    try { await fn(slug, ...args); toast.success("Done"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const purge = async (slug) => {
    try {
      await adminPurge(slug);
      setApps((xs) => xs.filter((x) => x.slug !== slug));
      toast.success("App permanently deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", "published", "suspended", "deleted"].map((s) => (
          <button key={s} data-testid={`admin-apps-filter-${s}`} onClick={() => setStatus(s)}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${status === s ? "bg-[#FF003C] border-[#FF003C] text-black" : "border-white/10 text-white/70 hover:border-white/30"}`}>
            {s}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full text-xs w-56" />
        </div>
      </div>
      <div className="space-y-2">
        {apps.map((a) => (
          <div key={a.slug} data-testid={`${ADMIN.appRow}-${a.slug}`} className="glass rounded-xl p-3">
            <div className="flex items-center gap-3">
              {a.icon_url ? <img src={a.icon_url} alt="" className="w-10 h-10 rounded-lg" /> : <div className="w-10 h-10 rounded-lg bg-white/5" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/app/${a.slug}`} className="font-heading font-bold text-sm hover:text-[#00F0FF] truncate">{a.name}</Link>
                  {a.is_featured && <span className="mono text-[9px] uppercase bg-[#FFE300]/15 text-[#FFE300] px-1.5 py-0.5 rounded">Featured</span>}
                  {a.is_editors_choice && <span className="mono text-[9px] uppercase bg-[#00F0FF]/15 text-[#00F0FF] px-1.5 py-0.5 rounded">EC</span>}
                  {a.status === "suspended" && <span className="mono text-[9px] uppercase bg-[#FF003C]/15 text-[#FF003C] px-1.5 py-0.5 rounded">Suspended</span>}
                  {a.status === "deleted" && <span className="mono text-[9px] uppercase bg-[#FF003C]/25 text-[#FF003C] px-1.5 py-0.5 rounded">Deleted</span>}
                </div>
                <div className="mono text-[10px] text-white/50">{a.developer} · {a.downloads} DL · {a.likes || 0} ♥ · {a.tips_received || 0} coins</div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <button data-testid={`admin-edit-${a.slug}`}
                  onClick={() => setEditing(editing === a.slug ? null : a.slug)}
                  className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> {editing === a.slug ? "Close" : "Edit"}
                </button>
                {a.status === "deleted" ? (
                  <>
                    <button data-testid="admin-restore-btn" onClick={() => act(adminRestore, a.slug)}
                      className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                    <PurgeButton app={a} onPurge={purge} />
                  </>
                ) : (
                  <>
                    {a.status === "suspended" ? (
                      <button data-testid={ADMIN.approveBtn} onClick={() => act(adminApprove, a.slug)}
                        className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10">Approve</button>
                    ) : (
                      <button data-testid={ADMIN.suspendBtn} onClick={() => {
                        const r = window.prompt("Reason?", "Policy violation");
                        if (r !== null) act(adminSuspend, a.slug, r);
                      }} className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10">Suspend</button>
                    )}
                    <button data-testid={ADMIN.featureBtn} onClick={() => act(a.is_featured ? adminUnfeature : adminFeature, a.slug)}
                      className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#FFE300]/40 text-[#FFE300] hover:bg-[#FFE300]/10 flex items-center gap-1">
                      <Star className="w-3 h-3" /> {a.is_featured ? "Unfeature" : "Feature"}
                    </button>
                    <button data-testid={ADMIN.editorsBtn} onClick={() => act(adminEditorsChoice, a.slug)}
                      className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10">EC</button>
                    <button data-testid="admin-delete-btn" onClick={() => {
                      const r = window.prompt(`Delete "${a.name}"? (soft — restorable)\n\nOptional reason:`, "Removed by moderator");
                      if (r === null) return;
                      act(adminSoftDelete, a.slug, r);
                    }} className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                    <PurgeButton app={a} onPurge={purge} />
                  </>
                )}
              </div>
            </div>
            {editing === a.slug && (
              <AdminAppEditor app={a} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
            )}
          </div>
        ))}
        {apps.length === 0 && <div className="mono text-xs text-white/40 text-center py-8">// no apps match</div>}
      </div>
    </div>
  );
}

function PurgeButton({ app, onPurge }) {
  const go = () => {
    if (!window.confirm(`Delete "${app.name}" forever?\n\nThis erases the app and every comment, tip, like and save for it. This cannot be undone.`)) return;
    const t = window.prompt(`Type "${app.slug}" to confirm`, "");
    if (t === null) return;
    if (t.trim() !== app.slug) { toast.error("Name did not match — cancelled"); return; }
    onPurge(app.slug);
  };
  return (
    <button data-testid={`admin-purge-btn-${app.slug}`} onClick={go}
      className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#FF003C]/60 text-[#FF003C] bg-[#FF003C]/5 hover:bg-[#FF003C]/20 flex items-center gap-1">
      <Flame className="w-3 h-3" /> Delete forever
    </button>
  );
}

function CommentsTab() {
  const [items, setItems] = useState([]);
  const load = () => adminReportedComments().then((d) => setItems(d.comments || []));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await adminDeleteComment(id); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clear = async (id) => {
    try { await adminClearReports(id); toast.success("Reports cleared"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mono text-xs text-white/60 mb-4">Reported comments ({items.length})</div>
      <div className="space-y-2">
        {items.length === 0 && <div className="glass rounded-xl p-6 text-center text-white/60 text-sm">No reports. Comments are chill.</div>}
        {items.map((c) => (
          <div key={c.id} data-testid={ADMIN.commentRow} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-bold text-sm">{c.user_name}</div>
                <div className="mono text-[10px] text-white/50">on <Link to={`/app/${c.slug}`} className="text-[#00F0FF]">{c.slug}</Link> · {c.reports?.length || 0} reports</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => clear(c.id)} className="mono text-[10px] uppercase px-2 py-1 rounded border border-white/15 text-white/70 hover:border-white/40 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Clear
                </button>
                <button onClick={() => del(c.id)} className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 flex items-center gap-1">
                  <X className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
            <div className="text-white/80 text-sm mt-2 whitespace-pre-wrap">"{c.body}"</div>
            {c.reports?.length > 0 && (
              <div className="mt-2 text-[10px] mono text-white/40">
                Reasons: {c.reports.map((r) => r.reason).filter(Boolean).join(" · ") || "(no reason)"}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CashoutsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => adminCashouts(status).then((d) => setItems(d.cashouts || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    const note = action === "reject" ? window.prompt("Reason (shown to user)?", "") : "";
    try { await adminCashoutAction(id, action, note); toast.success(action === "approve" ? "Approved" : "Rejected & refunded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${status === s ? "bg-[#39FF14] border-[#39FF14] text-black" : "border-white/10 text-white/70 hover:border-white/30"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="glass rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-[#39FF14]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm">{c.user_name} · ${c.usd}</div>
              <div className="mono text-[10px] text-white/50">{c.coins} coins - {c.fee_coins} fee = {c.net_coins} net · via {c.method} → {c.handle}</div>
              <div className="mono text-[9px] text-white/40 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            {c.status === "pending" ? (
              <div className="flex gap-1">
                <button data-testid={ADMIN.cashoutApprove} onClick={() => act(c.id, "approve")}
                  className="mono text-[10px] uppercase px-3 py-1.5 rounded border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Approve
                </button>
                <button data-testid={ADMIN.cashoutReject} onClick={() => act(c.id, "reject")}
                  className="mono text-[10px] uppercase px-3 py-1.5 rounded border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 flex items-center gap-1">
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            ) : (
              <span className="mono text-[10px] uppercase text-white/50">{c.status}</span>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="glass rounded-xl p-6 text-center text-white/60 text-sm">Nothing here.</div>}
      </div>
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [grantValues, setGrantValues] = useState({});   // {userId: string}

  const load = async () => {
    const { adminUsers } = await import("@/lib/api");
    const d = await adminUsers(q ? { q } : {});
    setItems(d.users || []);
  };
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const act = async (uid, updates, label) => {
    setBusyId(uid);
    try {
      const { adminUpdateUser } = await import("@/lib/api");
      const res = await adminUpdateUser(uid, updates);
      setItems((list) => list.map((x) => x.id === uid ? res.user : x));
      toast.success(label || "Updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name || u.email}"? This anonymises + bans them.`)) return;
    setBusyId(u.id);
    try {
      const { adminDeleteUser } = await import("@/lib/api");
      await adminDeleteUser(u.id);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const grant = async (u, sign) => {
    const raw = grantValues[u.id];
    const n = Number(raw);
    if (!n || Number.isNaN(n)) { toast.error("Enter a coin amount"); return; }
    await act(u.id, { coins_delta: sign * Math.abs(n) }, `${sign > 0 ? "Granted" : "Deducted"} ${Math.abs(n)} coins`);
    setGrantValues((v) => ({ ...v, [u.id]: "" }));
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full text-xs" />
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-white/50">{items.length} shown</div>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="glass rounded-xl p-6 text-center text-white/60 text-sm">No users match.</div>}
        {items.map((u) => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="glass rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-black" style={{ background: `${u.avatar_color || "#00F0FF"}22`, color: u.avatar_color || "#00F0FF", border: `1px solid ${u.avatar_color || "#00F0FF"}55` }}>
                {(u.name || u.email || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm truncate flex items-center gap-2">
                  {u.name || "—"}
                  {u.is_banned && <span className="mono text-[9px] uppercase bg-[#FF003C]/25 text-[#FF003C] px-1.5 py-0.5 rounded">banned</span>}
                  {u.role === "admin" && <span className="mono text-[9px] uppercase bg-[#FF003C]/15 text-[#FF003C] px-1.5 py-0.5 rounded">admin</span>}
                  {u.role === "developer" && <span className="mono text-[9px] uppercase bg-[#00F0FF]/15 text-[#00F0FF] px-1.5 py-0.5 rounded">dev</span>}
                </div>
                <div className="mono text-[10px] text-white/50 truncate">{u.email} · joined {(u.created_at || "").slice(0,10)}</div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <span className="mono text-[10px] text-[#FFE300] mr-1"><Coins className="w-3 h-3 inline" /> {u.coins ?? 0}</span>
                <button data-testid={`admin-user-expand-${u.id}`}
                  onClick={() => setExpanded(expanded === u.id ? null : u.id)}
                  className="mono text-[10px] uppercase px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10">
                  {expanded === u.id ? "Close" : "Manage"}
                </button>
              </div>
            </div>

            {expanded === u.id && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="mono text-[9px] uppercase tracking-widest text-white/50">Role:</span>
                  {["member","developer","admin"].map((r) => (
                    <button key={r} data-testid={`admin-user-role-${u.id}-${r}`}
                      onClick={() => act(u.id, { role: r }, `Role → ${r}`)}
                      disabled={busyId === u.id || u.role === r}
                      className={`mono text-[10px] uppercase px-2.5 py-1 rounded-full transition-colors ${
                        u.role === r ? "bg-[#00F0FF] text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"
                      }`}>{r}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="mono text-[9px] uppercase tracking-widest text-white/50">Coins:</span>
                  <input data-testid={`admin-user-grant-input-${u.id}`}
                    type="number" min={0} value={grantValues[u.id] || ""}
                    onChange={(e) => setGrantValues((v) => ({ ...v, [u.id]: e.target.value }))}
                    placeholder="amount" className="w-24 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-sm" />
                  <button data-testid={`admin-user-grant-${u.id}`} onClick={() => grant(u, +1)}
                    disabled={busyId === u.id || !grantValues[u.id]}
                    className="mono text-[10px] uppercase px-2.5 py-1 rounded-full bg-[#39FF14] text-black font-bold disabled:opacity-40">+ Grant</button>
                  <button data-testid={`admin-user-deduct-${u.id}`} onClick={() => grant(u, -1)}
                    disabled={busyId === u.id || !grantValues[u.id]}
                    className="mono text-[10px] uppercase px-2.5 py-1 rounded-full border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10 disabled:opacity-40">− Deduct</button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {u.id !== me?.id && (
                    <>
                      <button data-testid={`admin-user-ban-${u.id}`}
                        onClick={() => act(u.id, { is_banned: !u.is_banned }, u.is_banned ? "Unbanned" : "Banned")}
                        disabled={busyId === u.id}
                        className={`mono text-[10px] uppercase px-2.5 py-1 rounded-full ${
                          u.is_banned
                            ? "bg-[#39FF14] text-black font-bold"
                            : "border border-[#FF003C]/40 text-[#FF003C] hover:bg-[#FF003C]/10"
                        }`}>
                        {u.is_banned ? "Unban" : "Ban"}
                      </button>
                      <button data-testid={`admin-user-delete-${u.id}`} onClick={() => removeUser(u)}
                        disabled={busyId === u.id}
                        className="mono text-[10px] uppercase px-2.5 py-1 rounded-full border border-[#FF003C]/60 text-[#FF003C] hover:bg-[#FF003C]/20 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TvChannelsTab() {
  const [channels, setChannels] = useState([]);
  const [q, setQ] = useState("");
  const [showM3U, setShowM3U] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { tvList } = await import("@/lib/api");
      const d = await tvList({ limit: 500 });
      setChannels(d.channels || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeChannel = async (c) => {
    if (!window.confirm(`Delete channel "${c.name}"?`)) return;
    setBusyId(c.id);
    try {
      const { tvDelete } = await import("@/lib/api");
      await tvDelete(c.id);
      setChannels((list) => list.filter((x) => x.id !== c.id));
      toast.success("Channel removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const filtered = q
    ? channels.filter((c) => `${c.name} ${c.category || ""} ${c.country || ""}`.toLowerCase().includes(q.toLowerCase()))
    : channels;

  return (
    <div data-testid="admin-tv-tab">
      {/* Hero: Bulk Import CTA */}
      <div className="glass rounded-2xl p-5 mb-6 border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-transparent to-cyan-500/5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-1">// jixxeotv · admin</div>
            <div className="font-heading font-black uppercase tracking-tighter text-2xl">Bulk Channel Import</div>
            <div className="text-white/70 text-sm mt-1">
              Paste an <span className="text-cyan-300 mono">M3U/M3U8</span> playlist to add up to 1000 legal live TV channels at once. Duplicates are skipped automatically.
            </div>
          </div>
          <button data-testid="admin-m3u-btn" onClick={() => setShowM3U(true)}
            className="px-6 py-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-black mono text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-cyan-500/30">
            <Upload className="w-4 h-4" /> Bulk import M3U
          </button>
        </div>
      </div>

      {/* Channel list */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="mono text-[10px] uppercase tracking-widest text-white/60">
          {channels.length} channel{channels.length === 1 ? "" : "s"} · {filtered.length} shown
        </div>
        <div className="ml-auto relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input data-testid="admin-tv-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search channels…"
            className="w-full pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-full text-xs" />
        </div>
        <button data-testid="admin-tv-refresh" onClick={load}
          className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-white/30 flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading && <div className="glass rounded-xl p-6 text-center text-white/60 text-sm">Loading channels…</div>}
      {!loading && filtered.length === 0 && (
        <div data-testid="admin-tv-empty" className="glass rounded-xl p-8 text-center">
          <Tv className="w-8 h-8 mx-auto text-white/30 mb-2" />
          <div className="text-white/60 text-sm">No channels yet. Use bulk import above to add some.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`admin-tv-row-${c.id}`} className="glass rounded-xl p-3 flex items-center gap-3">
            {c.logo_url
              ? <img src={c.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover bg-black/40" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center"><Tv className="w-4 h-4 text-white/40" /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-sm truncate flex items-center gap-2">
                {c.name}
                {c.is_featured && <span className="mono text-[9px] uppercase bg-[#FFE300]/15 text-[#FFE300] px-1.5 py-0.5 rounded">Feat</span>}
                {c.live && <span className="mono text-[9px] uppercase bg-[#39FF14]/15 text-[#39FF14] px-1.5 py-0.5 rounded">Live</span>}
              </div>
              <div className="mono text-[10px] text-white/50 truncate">
                {[c.category, c.country, c.language].filter(Boolean).join(" · ") || "uncategorised"}
              </div>
              <div className="mono text-[9px] text-white/30 truncate">{c.stream_url}</div>
            </div>
            <button data-testid={`admin-tv-delete-${c.id}`} onClick={() => removeChannel(c)}
              disabled={busyId === c.id}
              className="mono text-[10px] uppercase px-2.5 py-1 rounded-full border border-[#FF003C]/60 text-[#FF003C] hover:bg-[#FF003C]/20 flex items-center gap-1 disabled:opacity-40">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>

      {showM3U && <M3UImportModal onClose={() => { setShowM3U(false); load(); }} />}
    </div>
  );
}

function M3UImportModal({ onClose }) {
  const [text, setText] = useState("");
  const [onlyHttps, setOnlyHttps] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste an M3U playlist first"); return; }
    setBusy(true); setResult(null);
    try {
      const { tvM3UImport } = await import("@/lib/api");
      const r = await tvM3UImport({ m3u: text, only_https: onlyHttps });
      setResult(r);
      toast.success(`Imported ${r.inserted} channel${r.inserted === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="admin-m3u-modal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#0b1220] border border-cyan-500/30 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">// jixxeotv · bulk import</div>
            <div className="font-heading font-black uppercase tracking-tighter text-lg">Paste an M3U playlist</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-white/60">
            Paste the raw content of an M3U/M3U8 playlist. Supports <code>#EXTINF</code> with <code>tvg-name</code>, <code>tvg-logo</code>, <code>tvg-country</code>, <code>tvg-language</code>, <code>group-title</code>. Duplicates are skipped automatically.
          </div>
          <textarea data-testid="admin-m3u-text" value={text} onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder={`#EXTM3U\n#EXTINF:-1 tvg-name="News" group-title="News",Channel Name\nhttps://example.com/stream.m3u8`}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mono text-xs" />
          <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
            <input data-testid="admin-m3u-https" type="checkbox" checked={onlyHttps} onChange={(e) => setOnlyHttps(e.target.checked)} />
            Only import HTTPS URLs (recommended)
          </label>
          <div className="rounded-lg p-3 border border-cyan-500/30 bg-cyan-500/5 mono text-[10px] text-cyan-300">
            // by importing you confirm every stream here is legally authorized. jixxeo does not host any content.
          </div>
          {result && (
            <div className="rounded-lg p-3 border border-[#39FF14]/40 bg-[#39FF14]/5 mono text-[10px] text-[#39FF14] space-y-1">
              <div>parsed: <span className="font-bold">{result.parsed}</span></div>
              <div>inserted: <span className="font-bold">{result.inserted}</span></div>
              <div>skipped (http): <span className="font-bold">{result.skipped_https}</span></div>
              <div>skipped (duplicate): <span className="font-bold">{result.skipped_duplicate}</span></div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 mono text-xs uppercase tracking-widest text-white/60">Close</button>
            <button data-testid="admin-m3u-submit" onClick={submit} disabled={busy || !text.trim()}
              className="px-5 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black font-black mono text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminAppEditor({ app, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: app.name || "",
    tagline: app.tagline || "",
    description: app.description || "",
    developer: app.developer || "",
    category: app.category || "",
    launch_url: app.launch_url || "",
    icon_url: app.icon_url || "",
    banner_url: app.banner_url || "",
    price: app.price ?? 0,
    is_free: !!app.is_free,
    is_pwa: !!app.is_pwa,
    is_featured: !!app.is_featured,
    is_trending: !!app.is_trending,
    is_new: !!app.is_new,
    is_editors_choice: !!app.is_editors_choice,
    tags: (app.tags || []).join(", "),
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const updates = { ...form };
      updates.tags = form.tags.split(",").map((s) => s.trim()).filter(Boolean);
      updates.price = Number(form.price) || 0;
      await adminEditApp(app.slug, updates);
      toast.success("App updated");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Row = ({ label, children }) => (
    <label className="block">
      <span className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
      {children}
    </label>
  );

  return (
    <div data-testid={`admin-editor-${app.slug}`} className="mt-3 pt-3 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-3">
      <Row label="Name">
        <input data-testid="admin-edit-name" value={form.name} onChange={set("name")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <Row label="Developer">
        <input value={form.developer} onChange={set("developer")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <Row label="Tagline">
        <input data-testid="admin-edit-tagline" value={form.tagline} onChange={set("tagline")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <Row label="Category">
        <input value={form.category} onChange={set("category")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <div className="md:col-span-2">
        <Row label="Description">
          <textarea data-testid="admin-edit-description" value={form.description} onChange={set("description")} rows={3}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
        </Row>
      </div>
      <Row label="Launch URL">
        <input value={form.launch_url} onChange={set("launch_url")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 mono text-xs" />
      </Row>
      <Row label="Icon URL">
        <div className="flex gap-2 items-center">
          <input data-testid="admin-edit-icon-url" value={form.icon_url} onChange={set("icon_url")}
            className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 mono text-xs" />
          {form.icon_url && <img data-testid="admin-edit-icon-preview" src={form.icon_url} alt="" className="w-8 h-8 rounded-md object-cover ring-1 ring-white/10" />}
        </div>
      </Row>
      <Row label="Banner URL">
        <div className="flex gap-2 items-center">
          <input data-testid="admin-edit-banner-url" value={form.banner_url} onChange={set("banner_url")}
            className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 mono text-xs" />
          {form.banner_url && <img data-testid="admin-edit-banner-preview" src={form.banner_url} alt="" className="w-16 h-8 rounded-md object-cover ring-1 ring-white/10" />}
        </div>
      </Row>
      <Row label="Price (coins)">
        <input type="number" value={form.price} onChange={set("price")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <Row label="Tags (comma-separated)">
        <input value={form.tags} onChange={set("tags")}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm" />
      </Row>
      <div className="flex flex-wrap gap-3 mono text-[10px] uppercase tracking-widest md:col-span-2">
        {["is_free","is_pwa","is_featured","is_trending","is_new","is_editors_choice"].map((k) => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="checkbox" checked={form[k]} onChange={set(k)} />
            {k.replace("is_", "").replace("_", " ")}
          </label>
        ))}
      </div>
      <div className="md:col-span-2 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-white/10 mono text-[10px] uppercase tracking-widest text-white/60">Cancel</button>
        <button data-testid="admin-edit-save" onClick={save} disabled={busy}
          className="px-4 py-1.5 rounded-full bg-[#00F0FF] hover:bg-white text-black font-black mono text-[10px] uppercase tracking-widest disabled:opacity-40 flex items-center gap-1">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
        </button>
      </div>
    </div>
  );
}

// ---- Pages editor (Privacy / Terms) --------------------------------------
function PagesTab() {
  const [pages, setPages] = useState([]);
  const [active, setActive] = useState("privacy");
  const [form, setForm] = useState({ title: "", body_md: "" });
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/pages");
      const list = r.data.pages || [];
      setPages(list);
      const doc = list.find((p) => p.slug === active) || list[0];
      if (doc) { setActive(doc.slug); setForm({ title: doc.title, body_md: doc.body_md }); }
    } catch { toast.error("Couldn't load pages"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pickPage = (slug) => {
    const doc = pages.find((p) => p.slug === slug);
    if (!doc) return;
    setActive(slug); setForm({ title: doc.title, body_md: doc.body_md });
  };

  const save = async () => {
    if (saveBusy) return;
    setSaveBusy(true);
    try {
      await api.patch(`/admin/pages/${active}`, form);
      toast.success("Saved — live on the site.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaveBusy(false); }
  };

  if (busy && !pages.length) return <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="admin-pages-tab" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {pages.map((p) => (
          <button key={p.slug} data-testid={`pages-tab-${p.slug}`} onClick={() => pickPage(p.slug)}
            className={"mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border " +
              (active === p.slug ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "border-white/10 bg-white/5 text-white/60 hover:text-white")}>
            {p.title}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl p-4 space-y-3">
        <label className="block">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Title</div>
          <input data-testid="pages-title" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2 text-sm outline-none" />
        </label>
        <label className="block">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Content (Markdown)</div>
          <textarea data-testid="pages-body" value={form.body_md} rows={22} onChange={(e) => setForm({ ...form, body_md: e.target.value })}
            className="w-full bg-white/5 border border-white/10 focus:border-[#00F0FF] rounded-xl px-3 py-2 text-sm outline-none font-mono resize-y" />
        </label>
        <div className="flex items-center gap-2">
          <Link to={`/${active}`} target="_blank" className="mono text-[10px] uppercase tracking-widest text-white/50 underline">Preview live →</Link>
          <button data-testid="pages-save" onClick={save} disabled={saveBusy}
            className="ml-auto inline-flex items-center gap-1 bg-[#00F0FF] text-black font-black mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40">
            {saveBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Contact Inbox -------------------------------------------------------
function InboxTab() {
  const [msgs, setMsgs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (filter === "unread") params.unread_only = true;
      if (filter === "starred") params.starred_only = true;
      const r = await api.get("/admin/contact", { params });
      setMsgs(r.data.messages || []);
      setUnread(r.data.unread_count || 0);
    } catch { toast.error("Couldn't load inbox"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markRead = async (m, next = true) => {
    try { await api.patch(`/admin/contact/${m.id}`, { is_read: next }); setMsgs((l) => l.map((x) => x.id === m.id ? { ...x, is_read: next } : x)); setUnread((n) => Math.max(0, n + (next ? -1 : 1))); }
    catch { toast.error("Couldn't update"); }
  };
  const toggleStar = async (m) => {
    const next = !m.is_starred;
    try { await api.patch(`/admin/contact/${m.id}`, { is_starred: next }); setMsgs((l) => l.map((x) => x.id === m.id ? { ...x, is_starred: next } : x)); }
    catch { toast.error("Couldn't update"); }
  };
  const del = async (m) => {
    if (!window.confirm(`Delete message from ${m.name}?`)) return;
    try { await api.delete(`/admin/contact/${m.id}`); setMsgs((l) => l.filter((x) => x.id !== m.id)); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div data-testid="admin-inbox-tab" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {["all", "unread", "starred"].map((f) => (
            <button key={f} data-testid={`inbox-filter-${f}`} onClick={() => setFilter(f)}
              className={"mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-full " +
                (filter === f ? "bg-[#00F0FF] text-black" : "text-white/60 hover:text-white")}>
              {f}{f === "unread" && unread > 0 ? ` (${unread})` : ""}
            </button>
          ))}
        </div>
        <div className="ml-auto mono text-[10px] uppercase tracking-widest text-white/50">{msgs.length} shown</div>
      </div>
      {busy ? (
        <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : msgs.length === 0 ? (
        <div className="py-12 text-center text-white/50" data-testid="inbox-empty">No messages{filter !== "all" ? ` in ${filter}` : ""}.</div>
      ) : (
        <ul className="space-y-2">
          {msgs.map((m) => (
            <li key={m.id} data-testid={`inbox-msg-${m.id}`}
              className={"glass rounded-2xl transition-colors " + (m.is_read ? "opacity-80" : "border-[#FF003C]/40")}>
              <button className="w-full text-left p-3 flex gap-3" onClick={() => { setExpanded(expanded === m.id ? null : m.id); if (!m.is_read) markRead(m, true); }}>
                <div className="w-9 h-9 shrink-0 rounded-lg bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center">
                  {m.is_read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-black text-sm truncate">{m.name}</span>
                    <span className="text-[11px] text-white/50 truncate">&lt;{m.email}&gt;</span>
                    {m.is_starred && <Star className="w-3 h-3 text-[#FFD700] shrink-0" fill="currentColor" />}
                  </div>
                  <div className="font-bold text-sm truncate">{m.subject}</div>
                  <div className="text-[11px] text-white/50 mt-0.5 truncate">{m.message}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </button>
              {expanded === m.id && (
                <div className="border-t border-white/10 p-3 space-y-2" data-testid={`inbox-expanded-${m.id}`}>
                  <div className="whitespace-pre-wrap text-sm text-white/80 leading-relaxed">{m.message}</div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                      className="inline-flex items-center gap-1 bg-[#00F0FF] text-black font-black mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                      Reply
                    </a>
                    <button data-testid={`inbox-star-${m.id}`} onClick={() => toggleStar(m)}
                      className={"inline-flex items-center gap-1 border font-black mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full " +
                        (m.is_starred ? "border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]" : "border-white/20 bg-white/5")}>
                      <Star className="w-3 h-3" fill={m.is_starred ? "currentColor" : "none"} /> {m.is_starred ? "Starred" : "Star"}
                    </button>
                    <button data-testid={`inbox-read-${m.id}`} onClick={() => markRead(m, !m.is_read)}
                      className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                      Mark {m.is_read ? "unread" : "read"}
                    </button>
                    <button data-testid={`inbox-delete-${m.id}`} onClick={() => del(m)}
                      className="ml-auto inline-flex items-center gap-1 border border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171] font-black mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Trivia admin tab — CRUD questions (category / difficulty / choices / correct)
// ---------------------------------------------------------------------------
const TRIVIA_CATEGORIES = ["science","tech","history","art","geography","math","literature","nature","food","sports","music","movies","general"];
const TRIVIA_DIFFICULTIES = ["easy","medium","hard"];
const _emptyQ = { text: "", choices: ["","","",""], correct_idx: 0, category: "general", difficulty: "easy" };

function TriviaAdminTab() {
  const [questions, setQuestions] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterDiff, setFilterDiff] = useState("all");
  const [editing, setEditing] = useState(null);   // question doc or "new"
  const [form, setForm] = useState(_emptyQ);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/trivia/questions?limit=500");
      setQuestions(r.data.questions || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error("Failed to load questions"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (questions || []).filter((x) => {
    if (q && !(`${x.text} ${x.category}`.toLowerCase().includes(q.toLowerCase()))) return false;
    if (filterCat !== "all" && x.category !== filterCat) return false;
    if (filterDiff !== "all" && x.difficulty !== filterDiff) return false;
    return true;
  });

  const openNew = () => { setEditing("new"); setForm(_emptyQ); };
  const openEdit = (row) => { setEditing(row); setForm({
    text: row.text, choices: [...row.choices, "", "", "", ""].slice(0, Math.max(4, row.choices.length)),
    correct_idx: row.correct_idx, category: row.category, difficulty: row.difficulty,
  }); };
  const closeEdit = () => { setEditing(null); setForm(_emptyQ); };

  const validate = () => {
    if (form.text.trim().length < 3) return "Question must be at least 3 characters";
    const cleaned = form.choices.map((c) => (c || "").trim()).filter(Boolean);
    if (cleaned.length < 2) return "Need at least 2 non-empty choices";
    if (form.correct_idx >= cleaned.length) return "Correct answer index is out of range";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const cleaned = form.choices.map((c) => (c || "").trim()).filter(Boolean);
      const body = {
        text: form.text.trim(), choices: cleaned,
        correct_idx: form.correct_idx, category: form.category, difficulty: form.difficulty,
      };
      if (editing === "new") {
        await api.post("/admin/trivia/questions", body);
        toast.success("Question added");
      } else {
        await api.patch(`/admin/trivia/questions/${editing.id}`, body);
        toast.success("Question updated");
      }
      await load(); closeEdit();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete this question?\n\n"${row.text}"`)) return;
    try {
      await api.delete(`/admin/trivia/questions/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="trivia-admin">
      <div className="rounded-3xl border-2 border-[#FF00E5]/40 bg-gradient-to-br from-[#FF00E5]/10 to-transparent p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[10rem]">
            <div className="font-black text-2xl">Trivia Question Bank</div>
            <div className="text-white/60 text-sm mt-1">{total} questions in bank · 10-question, 10s/question live game.</div>
          </div>
          <div className="flex gap-2">
            <button data-testid="trivia-admin-reload" onClick={load}
              className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10">Refresh</button>
            <button data-testid="trivia-admin-new" onClick={openNew}
              className="mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full bg-[#FF00E5] text-white font-black">+ New question</button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="trivia-admin-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search question or category…"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm" />
        </div>
        <select data-testid="trivia-admin-filter-cat" value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm mono uppercase text-[10px] tracking-widest">
          <option value="all">All categories</option>
          {TRIVIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="trivia-admin-filter-diff" value={filterDiff} onChange={(e) => setFilterDiff(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm mono uppercase text-[10px] tracking-widest">
          <option value="all">All difficulties</option>
          {TRIVIA_DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {editing && (
        <div className="rounded-3xl border-2 border-[#FF00E5]/40 bg-black/40 p-5 space-y-3" data-testid="trivia-admin-form">
          <div className="font-black text-lg">{editing === "new" ? "New question" : "Edit question"}</div>
          <textarea data-testid="trivia-admin-form-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
            rows={2} placeholder="Question text…"
            className="w-full bg-white/5 border border-white/10 rounded-2xl p-3 text-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {form.choices.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <button type="button" data-testid={`trivia-admin-form-correct-${i}`}
                  onClick={() => setForm({ ...form, correct_idx: i })}
                  className={"w-8 h-8 rounded-lg font-black text-xs flex items-center justify-center shrink-0 " +
                    (form.correct_idx === i ? "bg-[#39FF14] text-black" : "bg-white/10 text-white/60")}>
                  {String.fromCharCode(65 + i)}
                </button>
                <input data-testid={`trivia-admin-form-choice-${i}`} value={c}
                  onChange={(e) => { const cs = [...form.choices]; cs[i] = e.target.value; setForm({ ...form, choices: cs }); }}
                  placeholder={`Choice ${String.fromCharCode(65 + i)}${i < 2 ? " (required)" : " (optional)"}`}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm" />
              </div>
            ))}
          </div>
          <div className="text-[10px] mono uppercase tracking-widest text-white/50">Tap the letter to mark the correct answer · currently {String.fromCharCode(65 + form.correct_idx)}</div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="trivia-admin-form-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm mono uppercase text-[10px] tracking-widest">
              {TRIVIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="trivia-admin-form-diff" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm mono uppercase text-[10px] tracking-widest">
              {TRIVIA_DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="flex-1" />
            <button data-testid="trivia-admin-form-cancel" onClick={closeEdit}
              className="mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full border border-white/20 bg-white/5">Cancel</button>
            <button data-testid="trivia-admin-form-save" onClick={save} disabled={busy}
              className="mono text-[10px] uppercase tracking-widest px-5 py-2 rounded-full bg-[#39FF14] text-black font-black disabled:opacity-50">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Save"}
            </button>
          </div>
        </div>
      )}

      {!questions ? <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        filtered.length === 0 ? <div className="py-12 text-center text-white/50">No questions match your filters.</div> :
        <ul className="space-y-2" data-testid="trivia-admin-list">
          {filtered.map((row) => (
            <li key={row.id} data-testid={`trivia-admin-row-${row.id}`}
              className="rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-black text-sm">{row.text}</div>
                <div className="text-[10px] mono uppercase tracking-widest text-white/50 mt-1 flex flex-wrap gap-2">
                  <span className="px-2 py-0.5 rounded bg-white/5">{row.category}</span>
                  <span className={"px-2 py-0.5 rounded " +
                    (row.difficulty === "hard" ? "bg-[#F87171]/20 text-[#F87171]" :
                     row.difficulty === "medium" ? "bg-[#FFD700]/20 text-[#FFD700]" : "bg-[#39FF14]/20 text-[#39FF14]")}>{row.difficulty}</span>
                  <span>{row.choices.length} choices · answer {String.fromCharCode(65 + row.correct_idx)}</span>
                </div>
              </div>
              <button data-testid={`trivia-admin-edit-${row.id}`} onClick={() => openEdit(row)}
                className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/20 bg-white/5"><Pencil className="w-3 h-3 inline" /> Edit</button>
              <button data-testid={`trivia-admin-delete-${row.id}`} onClick={() => del(row)}
                className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#F87171]/30 bg-[#F87171]/10 text-[#F87171]"><Trash2 className="w-3 h-3 inline" /> Delete</button>
            </li>
          ))}
        </ul>}
    </div>
  );
}
